// Types

import { ValidationError } from "joi";
import isEqual from "lodash-es/isEqual";
import sortBy from "lodash-es/sortBy";
import { TSearchTransferRequest, IInventoryFilterOptions, TTransferType } from "../types";
import {
	TDealerInventoryTrade,
	TDisabledOptionsParams,
	TTransferInDefaultSearchTransferRequest,
	TTransferInOut,
} from "../types/dealerTrades";
import cloneDeep from "lodash-es/cloneDeep";
import compact from "lodash-es/compact";
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";
import { InventoryTab, TRANSFER_IN } from "../constants";
import { InventoryFilterItem } from "../contexts/InventoryFilterContext";

function isTransferIn(transferType: TTransferType) {
	return transferType === "Transfer In";
}

function isTransferOut(transferType: TTransferType) {
	return transferType === "Transfer Out";
}
function isTransferAssetSwap(transferType: TTransferType) {
	return transferType === "Asset Swap";
}

function createTransferCounterparts(rows: TDealerInventoryTrade[], locationId: string) {
	const receivingIds = new Map<string, string>();
	const givingIds = new Map<string, string>();
	rows.filter((item) => item.transferType === "Asset Swap").forEach((item) => {
		if (item.originLocationId === locationId) {
			givingIds.set(item.swapId, item._id);
		} else {
			receivingIds.set(item.swapId, item._id);
		}
	});

	const counterparts = new Map<string, string>();
	const allLinks = new Map<string, string>();

	for (const [swapId, _id] of givingIds.entries()) {
		const receivingId = receivingIds.get(swapId);
		if (receivingId) {
			counterparts.set(_id, receivingId);
			allLinks.set(_id, receivingId);
			allLinks.set(receivingId, _id);
		}
	}

	return { swapCounterparts: counterparts, links: allLinks };
}

const filterAndGroupInventoryOptions = ({
	inventoryFilterOptions,
	searchTransferRequest,
	destinationStoreFranchises,
	statusSelected,
}: {
	inventoryFilterOptions?: IInventoryFilterOptions[] | null;
	searchTransferRequest?: Partial<TSearchTransferRequest>;
	destinationStoreFranchises: string[];
	statusSelected: string[];
}) => {
	// Normalize data to uppercase for comparison
	const inventoryData =
		inventoryFilterOptions?.map((item) => ({
			...item,
			Make: item.Make?.toUpperCase(),
			Model: item.Model?.toUpperCase(),
			TRIM: item.TRIM?.toUpperCase(),
		})) ?? [];

	const { make = [], model = [], trim = [] } = searchTransferRequest ?? {};
	const makeUpperCase = make?.map((m: string) => m.toUpperCase()) ?? [];
	const modelUpperCase = model?.map((m: string) => m.toUpperCase()) ?? [];
	const trimUpperCase = trim?.map((t: string) => t.toUpperCase()) ?? [];

	// If status is not "Used" then find "origin" location makes that match "destination" location franchises
	const makesFilteredData = statusSelected.includes("Used")
		? inventoryData
		: inventoryData.filter((item) => destinationStoreFranchises.some((franchise) => item.Make?.includes(franchise)));
	const modelFilteredData = makesFilteredData.filter((item) => makeUpperCase?.some((m) => m === item.Make));
	const trimFilteredData = modelFilteredData.filter((item) => modelUpperCase?.some((m) => m === item.Model));
	const yearFilteredData = trimFilteredData.filter((item) => trimUpperCase?.some((t) => t === item.TRIM));

	// Get unique options array for selectors
	const toOptions = (arr: (string | undefined)[]) =>
		[...new Set(arr)]
			.filter(Boolean)
			.map((val) => ({ label: val, value: val }))
			.sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));

	return {
		locationId: [...new Set(inventoryData.map((item) => item.locationIds))],
		make: toOptions(makesFilteredData.map((item) => item.Make)),
		model: toOptions(modelFilteredData.map((item) => item.Model)),
		trim: toOptions(trimFilteredData.map((item) => item.TRIM)),
		year: toOptions(yearFilteredData.map((item) => item.Year)),
	};
};

const findError = (errors: ValidationError | null, path: (string | number)[]) => {
	return errors?.details?.find((item) => JSON.stringify(item?.path) === JSON.stringify(path));
};

const formatSelected = (data: string[] = []) => {
	return data?.map((item: string) => ({ label: item, value: item })) ?? [];
};

const groupMakesByLocation = (data?: IInventoryFilterOptions[] | null) => {
	const result: { locationId: string; Make: string[] }[] = [];

	data?.forEach((item) => {
		const make = item.Make;
		if (item.Type === "New") {
			item.locationIds.forEach((locationId) => {
				let locationGroup = result.find((group) => group.locationId === locationId);

				if (!locationGroup) {
					locationGroup = { locationId, Make: [] };
					result.push(locationGroup);
				}

				if (!locationGroup.Make.includes(make)) {
					locationGroup.Make.push(make);
				}
			});
		}
	});

	return result;
};

const keepLastDestination = (
	data: {
		value: string;
		group: string;
	}[]
) => {
	const origin = data.find((item) => item.group === "origin");
	const destinations = data.filter((item) => item.group === "destination");
	const lastDestination = destinations[destinations.length - 1];

	return compact([origin, lastDestination]);
};

const stringArraysEqualIgnoreOrder = (arr1: string[], arr2: string[]) => {
	if (!!arr1?.length !== !!arr2?.length) {
		return false;
	}

	const sortedArr1 = sortBy(arr1);
	const sortedArr2 = sortBy(arr2);

	return isEqual(sortedArr1, sortedArr2);
};

const arraysEqualIgnoreOrder = (
	arr1: {
		value: string;
		group: string;
	}[],
	arr2: {
		value: string;
		group: string;
	}[]
) => {
	if (!!arr1?.length !== !!arr2?.length) {
		return false;
	}

	const sortedArr1 = sortBy(arr1, ["value", "group"]);
	const sortedArr2 = sortBy(arr2, ["value", "group"]);

	return isEqual(sortedArr1, sortedArr2);
};

const hasIntersection = (arrayA: string[], arrayB: string[]) => {
	for (const a of arrayA) {
		if (arrayB.some((b) => a?.includes(b) || b?.includes(a))) {
			return true;
		}
	}
	for (const b of arrayB) {
		if (arrayA.some((a) => b?.includes(a) || a?.includes(b))) {
			return true;
		}
	}
	return false;
};

const getDisabledOptions = ({
	locationId,
	locations,
	franchisesByLocations,
	storeFranchises,
	status,
	disableLocationOptions,
}: TDisabledOptionsParams) => {
	const isUsedSelected = status?.includes("Used");
	const result = locations.filter((loc) => {
		if (disableLocationOptions) {
			return true;
		}
		const locFranchises = franchisesByLocations.find((item) => item.locationId === loc.value)?.franchises ?? [];
		const hasIntersectionFranchises = hasIntersection(locFranchises, storeFranchises);
		const haveSomeFranchise = isUsedSelected ? true : hasIntersectionFranchises;
		return loc.value === locationId || !haveSomeFranchise;
	});
	return result.map((loc) => loc.value);
};

const filterObjectKeys = <T extends Record<string, unknown>>(obj: T, keysToKeep: string[]): Partial<T> => {
	const clonedObj = cloneDeep(obj);
	return Object.fromEntries(Object.entries(clonedObj).filter(([key]) => keysToKeep?.includes(key))) as Partial<T>;
};

const allStringsExist = (source: string[], target: string[]): boolean => {
	return source.every((str) => target.includes(str));
};

const checkAppendInMultiSelect = ({
	key,
	value,
	searchTransferRequest,
}: {
	key: keyof TSearchTransferRequest;
	value: TSelectValue | string | string[];
	searchTransferRequest: Partial<TSearchTransferRequest>;
}) => {
	if (key === "location") {
		const storedLocationsId = searchTransferRequest[key]?.filter((item) => item.group === "origin")?.map((loc) => loc.value) ?? [];
		const newLocationsId = (value as TSelectValue[])?.map((loc) => (loc as { value: string })?.value) ?? [];
		return allStringsExist(storedLocationsId, newLocationsId);
	}
	const storedArray = (searchTransferRequest[key] ?? []) as string[];
	const newArray = (value ?? []) as string[];
	return allStringsExist(storedArray, newArray);
};

const resetFields = ({
	key,
	value,
	transferType,
	locationsOptions,
	locationId,
	isAppend,
	defaultSearchTransferRequest,
	disableLocationOptionsAndValidation,
}: {
	key: keyof TSearchTransferRequest;
	value: Partial<TSearchTransferRequest>;
	transferType: TTransferInOut;
	locationId: string;
	isAppend?: boolean;
	locationsOptions: {
		isLoadingLocations: boolean;
		mappedLocations: { value: string; label: string }[];
	};
	defaultSearchTransferRequest?: Partial<TSearchTransferRequest>;
	disableLocationOptionsAndValidation?: boolean;
}) => {
	const base = ["status", "location", "vinNumber", "stockNumber"];
	const transferRequestMap: Partial<Record<keyof TSearchTransferRequest, Function>> = {
		status: () => {
			if (disableLocationOptionsAndValidation) {
				return isAppend ? { ...value } : { location: defaultSearchTransferRequest?.location };
			}
			if (isTransferIn(transferType)) {
				const location = [{ ...locationsOptions.mappedLocations.find((loc) => loc.value === locationId), group: "destination" }];
				return isAppend ? { ...value } : { location };
			} else {
				const location = [{ ...locationsOptions.mappedLocations.find((loc) => loc.value === locationId), group: "origin" }];
				return isAppend ? { ...value } : { location };
			}
		},
		location: () => {
			const newValue = filterObjectKeys(value, ["status"]);
			return resolveValue(value, newValue, isAppend);
		},
		make: () => {
			const newValue = filterObjectKeys(value, base);
			return resolveValue(value, newValue, isAppend);
		},
		model: () => {
			const newValue = filterObjectKeys(value, [...base, "make"]);
			return resolveValue(value, newValue, isAppend);
		},
		trim: () => {
			const newValue = filterObjectKeys(value, [...base, "make", "model"]);
			return resolveValue(value, newValue, isAppend);
		},
		year: () => {
			const newValue = filterObjectKeys(value, [...base, "make", "model", "trim"]);
			return resolveValue(value, newValue, isAppend);
		},
		stockNumber: () => {
			return value;
		},
		vinNumber: () => {
			return value;
		},
	};

	const action = transferRequestMap[key];
	if (action) {
		return action();
	}
	return {};
};

const resolveValue = (value: Partial<TSearchTransferRequest>, newValue: Partial<TSearchTransferRequest>, isAppend?: boolean) => {
	return isAppend ? { ...value } : { ...newValue };
};

const getOptions = (transferType: TTransferInOut, locationId: string, mappedLocations: { value: string; label: string }[]) => {
	const result = mappedLocations.map((loc) => {
		const isTransferInType = isTransferIn(transferType);
		const isOrigin = isTransferInType ? loc.value !== locationId : loc.value === locationId;
		const group = isOrigin ? "origin" : "destination";
		return { ...loc, group };
	});

	return result.sort((a, b) => (isTransferIn(transferType) ? a.group.localeCompare(b.group) : b.group.localeCompare(a.group)));
};

const resolveDisableStockVINandMake = (transferType: TTransferInOut, searchTransferRequest: Partial<TSearchTransferRequest>) => {
	if (!searchTransferRequest.status?.length) {
		return true;
	}
	if (isTransferIn(transferType)) {
		const location = searchTransferRequest.location?.find((loc) => loc.group === "origin");
		if (!location) {
			return true;
		}
	} else {
		const location = searchTransferRequest.location?.find((loc) => loc.group === "destination");
		if (!location) {
			return true;
		}
	}
	return false;
};

const getStores = ({
	locationsSelected,
	locationsOptions,
}: {
	locationsSelected?: { value: string; group: string }[];
	locationsOptions: { mappedLocations: { value: string; label: string }[] };
}) => {
	return locationsSelected
		?.map((item) => ({
			label: locationsOptions.mappedLocations.find((loc) => loc.value === item.value)?.label,
			value: item.value,
			group: item.group,
		}))
		.sort((a, b) => b.group.localeCompare(a.group));
};

const resolveFiltersDefault = (itemSelected?: TDealerInventoryTrade) => {
	const itemDefault = {
		status: [],
		location: [],
		make: [],
		model: [],
		trim: [],
		year: [],
		stockNumber: "",
		vinNumber: "",
	} as unknown as TSearchTransferRequest;

	if (!itemSelected) {
		return itemDefault;
	}

	const { vehicle } = itemSelected;

	itemDefault.status = [vehicle?.type];
	itemDefault.location = [
		{ value: itemSelected.originLocationId, group: "origin" },
		{ value: itemSelected.destinationLocationId, group: "destination" },
	];
	itemDefault.make = [vehicle?.make];
	return itemDefault;
};

const reorderInventoryTrades = (a: TDealerInventoryTrade, b: TDealerInventoryTrade) => {
	const aMatch = a.requestedByLocationId === a.destinationLocationId;
	const bMatch = b.requestedByLocationId === b.destinationLocationId;

	if (aMatch && !bMatch) {
		return -1; // Move `a` to the front
	} else if (!aMatch && bMatch) {
		return 1; // Move `b` to the front
	} else {
		return 0; // Maintain the relative order otherwise
	}
};

const removeFalsyFilters = (filters: Partial<TSearchTransferRequest>) => {
	const cloneDeepFilters = cloneDeep(filters);
	Object.keys(cloneDeepFilters).forEach((key) => {
		const typedKey = key as keyof TSearchTransferRequest;
		const entry = cloneDeepFilters[typedKey];
		if (Array.isArray(entry) && !entry.length) {
			delete cloneDeepFilters[typedKey];
		}
		if (!entry) {
			delete cloneDeepFilters[typedKey];
		}
	});
	return cloneDeepFilters;
};

const resolveDisableOptions = (filterItems: InventoryFilterItem[]) => {
	const filteredItems = !!filterItems?.filter((item) => item.origin !== InventoryTab.DEALER_TRADES)?.length;
	if (filteredItems) {
		return [TRANSFER_IN];
	}
	return [];
};

const resolveDefaultTransferRequest = (
	uniqueVinNumbers: string[],
	defaultLocation: {
		value: string;
		group: string;
	}[]
): TTransferInDefaultSearchTransferRequest | undefined => {
	if (uniqueVinNumbers?.length) {
		return { status: ["New", "Used"], location: defaultLocation, vinNumber: uniqueVinNumbers };
	}
};

export {
	resolveDefaultTransferRequest,
	resolveDisableOptions,
	removeFalsyFilters,
	reorderInventoryTrades,
	getStores,
	resolveDisableStockVINandMake,
	getOptions,
	resetFields,
	hasIntersection,
	getDisabledOptions,
	stringArraysEqualIgnoreOrder,
	arraysEqualIgnoreOrder,
	isTransferIn,
	isTransferOut,
	isTransferAssetSwap,
	filterAndGroupInventoryOptions,
	findError,
	formatSelected,
	groupMakesByLocation,
	keepLastDestination,
	createTransferCounterparts,
	resolveFiltersDefault,
	allStringsExist,
	checkAppendInMultiSelect,
};
